import * as THREE from 'three';
import { FootMeshData } from 'context/FootMeshContext';
import { MeshConfig } from 'types/stl';

// Ported from PT-SCAN3D (Form1.barbtnImport3D_ItemClick / Editor3D.AlignFoot / Xtra3DImport.FlipEntity).
// Picked foot points, in order:
export const FOOT_POINT_LABELS = ['Heel', '1st Met Head (medial)', '5th Met Head (lateral)'];

// Frame with its origin at `origin`, X toward `xPoint` and Y as close as possible to `up`
// (used like the planes of Eyeshot Transformation.Rotation(p, p2)).
const frameMatrix = (origin: THREE.Vector3, xPoint: THREE.Vector3, up: THREE.Vector3): THREE.Matrix4 => {
  const xAxis = xPoint.clone().sub(origin).normalize();
  const zAxis = xAxis.clone().cross(up).normalize();
  const yAxis = zAxis.clone().cross(xAxis);
  return new THREE.Matrix4().makeBasis(xAxis, yAxis, zAxis).setPosition(origin);
};

/**
 * Guesses the outward direction of the sole of a foot scan in any orientation.
 * The foot length is the largest bounding box dimension; of the 4 remaining box faces, the sole is the
 * one with the most mesh surface lying flat against it.
 * Also returns the length axis, to use as the screen "up" when looking at the sole.
 */
export const findSoleDirection = (geometry: THREE.BufferGeometry): { sole: THREE.Vector3; length: THREE.Vector3 } => {
  if (!geometry.boundingBox) geometry.computeBoundingBox();
  const box = geometry.boundingBox!;
  const size = box.getSize(new THREE.Vector3()).toArray();
  const lengthAxis = size.indexOf(Math.max(...size));
  const sideAxes = [0, 1, 2].filter(axis => axis !== lengthAxis);
  // Candidate faces: [axis, sign], e.g. [1, -1] is the box face at min Y.
  const faces = sideAxes.flatMap(axis => [[axis, -1], [axis, 1]]);
  const areas = faces.map(() => 0);

  const position = geometry.getAttribute('position');
  const index = geometry.index;
  const triangleCount = (index ? index.count : position.count) / 3;
  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3();
  const triangle = new THREE.Triangle(a, b, c);
  const centroid = new THREE.Vector3();
  const min = box.min.toArray(), max = box.max.toArray();
  for (let t = 0; t < triangleCount; t++) {
    const vertex = (k: number) => (index ? index.getX(t * 3 + k) : t * 3 + k);
    a.fromBufferAttribute(position, vertex(0));
    b.fromBufferAttribute(position, vertex(1));
    c.fromBufferAttribute(position, vertex(2));
    triangle.getMidpoint(centroid);
    const p = centroid.toArray();
    faces.forEach(([axis, sign], i) => {
      const distance = sign < 0 ? p[axis] - min[axis] : max[axis] - p[axis];
      if (distance < size[axis] * 0.05) areas[i] += triangle.getArea();
    });
  }

  const [soleAxis, soleSign] = faces[areas.indexOf(Math.max(...areas))];
  const sole = new THREE.Vector3().setComponent(soleAxis, soleSign);
  const length = new THREE.Vector3().setComponent(lengthAxis, 1);
  return { sole, length };
};

// Insole length range accepted by the property panel, in mm.
const MIN_INSOLE_LENGTH_MM = 150;
const MAX_INSOLE_LENGTH_MM = 450;

/** Insole length (mm) for a foot: the Y extent of the imported mesh, clamped to the valid insole range. */
export const getInsoleLengthForFoot = (foot: FootMeshData): number => {
  if (!foot.geometry.boundingBox) foot.geometry.computeBoundingBox();
  const length = foot.geometry.boundingBox!.getSize(new THREE.Vector3()).y;
  return Math.round(Math.min(Math.max(length + 10, MIN_INSOLE_LENGTH_MM), MAX_INSOLE_LENGTH_MM));
};

/**
 * Mirrors a foot to stand in for the missing opposite foot (FlipEntity).
 * Points are mirrored too, so heel/medial/lateral keep their meaning and the alignment still works.
 */
export const mirrorFoot = (foot: FootMeshData): FootMeshData => {
  const mirror = new THREE.Matrix4().makeScale(-1, 1, 1);
  const geometry = (foot.geometry.index ? foot.geometry.toNonIndexed() : foot.geometry.clone()).applyMatrix4(mirror);
  // Mirroring inverts the winding; swap two vertices of each triangle to keep normals outward.
  const position = geometry.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < position.count; i += 3) {
    const x = position.getX(i + 1), y = position.getY(i + 1), z = position.getZ(i + 1);
    position.setXYZ(i + 1, position.getX(i + 2), position.getY(i + 2), position.getZ(i + 2));
    position.setXYZ(i + 2, x, y, z);
  }
  geometry.computeVertexNormals();
  return {
    fileName: `${foot.fileName} (mirrored)`,
    geometry,
    points: foot.points.map(p => p.clone().applyMatrix4(mirror)),
    // A saved foot has no points to re-align with; the insoles are mirrored about x = 0,
    // so its mirror already sits on the other insole.
    placed: foot.placed,
  };
};

// Heel center and forefoot center of an insole (low-poly geometry), at the heel center height.
const insoleReferencePoints = (insoleGeometry: THREE.BufferGeometry, meshConfig: MeshConfig) => {
  const { heelHeight, forefootMedialWedge, forefootLateralWedge } = meshConfig.joints.surface;
  const positions = insoleGeometry.getAttribute('position');
  const vertex = (id: number) => new THREE.Vector3().fromBufferAttribute(positions, id);
  const heelCenter = vertex(heelHeight.centerId);
  const forefootCenter = vertex(forefootMedialWedge.centerId)
    .add(vertex(forefootLateralWedge.centerId))
    .multiplyScalar(0.5)
    .setY(heelCenter.y);
  return { heelCenter, forefootCenter };
};

/**
 * Computes the matrix that places a foot mesh on an insole (AlignFoot).
 * Foot frame: heel -> middle of the met heads, with "up" being the normal of the sole plane
 * (heel, met heads) on the side where the foot body is.
 * Insole frame: heel center -> middle of the forefoot medial/lateral joints, with world +Y as "up",
 * all taken at the heel center height so the foot sole rests on the insole top surface.
 * The up direction does not depend on which met head was picked first, so swapping medial/lateral
 * (easy to do on a left foot seen from below) cannot turn the foot upside down.
 */
export const computeFootAlignMatrix = (
  foot: FootMeshData,
  insoleGeometry: THREE.BufferGeometry,
  meshConfig: MeshConfig
): THREE.Matrix4 | null => {
  const { heelHeight, forefootMedialWedge, forefootLateralWedge } = meshConfig.joints.surface;
  if (foot.points.length < 3 || !heelHeight || !forefootMedialWedge || !forefootLateralWedge) return null;

  const { heelCenter, forefootCenter } = insoleReferencePoints(insoleGeometry, meshConfig);

  const [heel, metA, metB] = foot.points;
  const metCenter = metA.clone().add(metB).multiplyScalar(0.5);
  const footUp = metA.clone().sub(heel).cross(metB.clone().sub(heel)).normalize();
  if (!foot.geometry.boundingBox) foot.geometry.computeBoundingBox();
  const footBodyCenter = foot.geometry.boundingBox!.getCenter(new THREE.Vector3());
  if (footBodyCenter.sub(heel).dot(footUp) < 0) footUp.negate();

  const footFrame = frameMatrix(heel, metCenter, footUp);
  const insoleFrame = frameMatrix(heelCenter, forefootCenter, new THREE.Vector3(0, 1, 0));

  return insoleFrame.multiply(footFrame.invert());
};

// Foot vertices this close to the lowest foot point form the footprint (mm).
const FOOTPRINT_HEIGHT_MM = 10;

// Height of the top surface of `geometry` at (x, z): the highest triangle hit by a vertical ray, or null.
const surfaceHeightAt = (geometry: THREE.BufferGeometry, x: number, z: number): number | null => {
  const position = geometry.getAttribute('position');
  const index = geometry.index;
  const triangleCount = (index ? index.count : position.count) / 3;
  geometry.computeBoundingBox(); // the insole may have been edited since it was last computed
  const ray = new THREE.Ray(new THREE.Vector3(x, geometry.boundingBox!.max.y + 1, z), new THREE.Vector3(0, -1, 0));
  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3(), hit = new THREE.Vector3();
  let top: number | null = null;
  for (let t = 0; t < triangleCount; t++) {
    const vertex = (k: number) => (index ? index.getX(t * 3 + k) : t * 3 + k);
    a.fromBufferAttribute(position, vertex(0));
    b.fromBufferAttribute(position, vertex(1));
    c.fromBufferAttribute(position, vertex(2));
    if (ray.intersectTriangle(a, b, c, false, hit) && (top === null || hit.y > top)) top = hit.y;
  }
  return top;
};

const transformedVertices = (geometry: THREE.BufferGeometry, matrix: THREE.Matrix4): THREE.Vector3[] => {
  const position = geometry.getAttribute('position');
  const points: THREE.Vector3[] = new Array(position.count);
  for (let i = 0; i < position.count; i++) points[i] = new THREE.Vector3().fromBufferAttribute(position, i).applyMatrix4(matrix);
  return points;
};

// Main direction of points on the horizontal (XZ) plane, as a unit vector with y = 0.
const principalHorizontalAxis = (points: THREE.Vector3[]): THREE.Vector3 => {
  let mx = 0, mz = 0;
  points.forEach(p => { mx += p.x; mz += p.z; });
  mx /= points.length; mz /= points.length;
  let sxx = 0, szz = 0, sxz = 0;
  points.forEach(p => {
    const dx = p.x - mx, dz = p.z - mz;
    sxx += dx * dx; szz += dz * dz; sxz += dx * dz;
  });
  const angle = 0.5 * Math.atan2(2 * sxz, sxx - szz);
  return new THREE.Vector3(Math.cos(angle), 0, Math.sin(angle));
};

const minHeight = (points: THREE.Vector3[]) => {
  let min = Infinity;
  points.forEach(p => { if (p.y < min) min = p.y; });
  return min;
};

/**
 * Improves the 3-point placement from computeFootAlignMatrix:
 * 1. Turns the foot about the vertical axis, around its heel point, so the footprint's long axis matches the insole's.
 * 2. Moves the foot horizontally so its heel point is on the insole heel center (the pressure alignment
 *    translation point).
 * The height is set afterwards by seatFootOnInsole.
 * `insoleGeometry` is the rendered insole geometry, in scene coordinates.
 */
export const refineFootAlignMatrix = (
  foot: FootMeshData,
  initialMatrix: THREE.Matrix4,
  insoleGeometry: THREE.BufferGeometry,
  heelToToe: THREE.Vector3,
  insoleHeelCenter: THREE.Vector3
): THREE.Matrix4 => {
  const matrix = initialMatrix.clone();
  const insolePoints = transformedVertices(insoleGeometry, new THREE.Matrix4());
  const footPoints = transformedVertices(foot.geometry, matrix);
  const footprint = () => {
    const minY = minHeight(footPoints);
    return footPoints.filter(p => p.y < minY + FOOTPRINT_HEIGHT_MM);
  };
  const applyToFoot = (step: THREE.Matrix4) => {
    matrix.premultiply(step);
    footPoints.forEach(p => p.applyMatrix4(step));
  };

  // 1. Long axes. Both point from heel to toe. The heel point is the pivot, so it stays in place.
  const insoleAxis = principalHorizontalAxis(insolePoints);
  if (insoleAxis.dot(heelToToe) < 0) insoleAxis.negate();
  const printPoints = footprint();
  const footAxis = principalHorizontalAxis(printPoints);
  if (footAxis.dot(insoleAxis) < 0) footAxis.negate();
  const yaw = Math.atan2(footAxis.clone().cross(insoleAxis).y, footAxis.dot(insoleAxis));
  const heel = foot.points[0].clone().applyMatrix4(matrix);
  applyToFoot(new THREE.Matrix4().makeTranslation(heel.x, 0, heel.z)
    .multiply(new THREE.Matrix4().makeRotationY(yaw))
    .multiply(new THREE.Matrix4().makeTranslation(-heel.x, 0, -heel.z)));

  // 2. Heel point onto the insole heel center (horizontally; the height comes from seatFootOnInsole).
  applyToFoot(new THREE.Matrix4().makeTranslation(insoleHeelCenter.x - heel.x, 0, insoleHeelCenter.z - heel.z));

  return matrix;
};

/**
 * Places a foot on an insole: 3-point placement (computeFootAlignMatrix), then refineFootAlignMatrix.
 * `lowpolyGeometry` holds the joint vertices; `insoleGeometry` is the rendered insole surface.
 */
export const alignFootToInsole = (
  foot: FootMeshData,
  lowpolyGeometry: THREE.BufferGeometry,
  insoleGeometry: THREE.BufferGeometry,
  meshConfig: MeshConfig
): THREE.Matrix4 | null => {
  const initialMatrix = computeFootAlignMatrix(foot, lowpolyGeometry, meshConfig);
  if (!initialMatrix) return null;
  const { heelCenter, forefootCenter } = insoleReferencePoints(lowpolyGeometry, meshConfig);
  // Same heel center as the pressure / 3D foot alignment translation point.
  const alignmentHeelCenter = new THREE.Vector3().fromBufferAttribute(lowpolyGeometry.getAttribute('position'), meshConfig.pressureAlignmentDefault[0]);
  return refineFootAlignMatrix(foot, initialMatrix, insoleGeometry, forefootCenter.sub(heelCenter), alignmentHeelCenter);
};

/**
 * Sets the foot height: moves the foot (placed by `matrix`) vertically so the bottom of its bounding box (min Y)
 * is at the rendered insole top surface at the heel center (the pressure alignment translation point).
 * Falls back to the heel height joint if the surface is not found there.
 * Applied to every foot (new, saved or manually adjusted), since manual alignment only moves and turns it horizontally.
 */
export const seatFootOnInsole = (
  foot: FootMeshData,
  matrix: THREE.Matrix4,
  lowpolyGeometry: THREE.BufferGeometry,
  insoleGeometry: THREE.BufferGeometry,
  meshConfig: MeshConfig
): THREE.Matrix4 => {
  const positions = lowpolyGeometry.getAttribute('position');
  const heelCenter = new THREE.Vector3().fromBufferAttribute(positions, meshConfig.pressureAlignmentDefault[0]);
  const heelHeightY = new THREE.Vector3().fromBufferAttribute(positions, meshConfig.joints.surface.heelHeight.centerId).y;
  const surfaceY = surfaceHeightAt(insoleGeometry, heelCenter.x, heelCenter.z) ?? heelHeightY;
  const footMinY = minHeight(transformedVertices(foot.geometry, matrix));
  return matrix.clone().premultiply(new THREE.Matrix4().makeTranslation(0, surfaceY - footMinY, 0));
};
